import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { FamilyContactDialog } from "@/components/FamilyContactDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserX, PhoneCall, CheckCircle2, Phone } from "lucide-react";

const br = (d) => (d ? d.split("-").reverse().join("/") : "");
const CH = { TELEFONE: "Telefone", WHATSAPP: "WhatsApp", PRESENCIAL: "Presencial", EMAIL: "E-mail" };

function AlertCard({ a, onContact }) {
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`alert-card-${a.id}`}>
      <div className="min-w-0">
        <Link to={`/alunos/${a.student_id}`} className="font-semibold text-slate-900 hover:text-orange-600" data-testid={`alert-student-link-${a.id}`}>{a.student_name}</Link>
        <div className="text-xs text-slate-500">
          {a.class_name || "Sem turma"}{a.registration ? ` • Matrícula ${a.registration}` : ""}
        </div>
        <div className="mt-1 text-xs text-slate-600">
          Faltas: {(a.days || []).slice().reverse().map(br).join(", ")}
        </div>
        {a.guardian_name && (
          <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <Phone className="h-3 w-3" />{a.guardian_name}{a.guardian_phone ? ` • ${a.guardian_phone}` : ""}
          </div>
        )}
        {a.contact && (
          <div className="mt-2 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800" data-testid={`alert-contact-info-${a.id}`}>
            <div className="flex items-center gap-1 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" />{CH[a.contact.channel] || a.contact.channel} • {new Date(a.contact.at).toLocaleString("pt-BR")} • {a.contact.by}</div>
            <div className="mt-0.5 break-words">{a.contact.note}</div>
          </div>
        )}
      </div>
      {!a.contact && (
        <Button onClick={() => onContact(a)} className="shrink-0 bg-orange-500 hover:bg-orange-600" data-testid={`alert-contact-btn-${a.id}`}>
          <PhoneCall className="mr-2 h-4 w-4" />Registrar contato
        </Button>
      )}
    </Card>
  );
}

export default function AlertasFaltas() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [tab, setTab] = useState("PENDENTE");
  const [data, setData] = useState({ alerts: [], pending: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);
  const [contactFor, setContactFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/absence-alerts", { params: { ...scopeParams, status: tab } })
      .then((r) => setData(r.data)).catch(() => setData({ alerts: [], pending: 0, contacted: 0 }))
      .finally(() => setLoading(false));
  }, [tab, scopeParams]);
  useEffect(() => { load(); }, [load, selectedSchoolId]);

  return (
    <div data-testid="alertas-faltas-page">
      <PageHeader title="Alertas de Faltas" subtitle="Estudantes com 3 dias letivos seguidos de falta e o acompanhamento com as famílias" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="PENDENTE" data-testid="alerts-tab-pending">Pendentes <Badge className="ml-2 bg-orange-500" data-testid="alerts-pending-count">{data.pending}</Badge></TabsTrigger>
          <TabsTrigger value="CONTATADO" data-testid="alerts-tab-contacted">Contato feito <Badge variant="outline" className="ml-2" data-testid="alerts-contacted-count">{data.contacted}</Badge></TabsTrigger>
        </TabsList>
      </Tabs>
      {loading ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
        : data.alerts.length === 0 ? (
          <EmptyState icon={UserX} title={tab === "PENDENTE" ? "Nenhum alerta pendente" : "Nenhum contato registrado"}
            description={tab === "PENDENTE" ? "Todos os estudantes em alerta já tiveram contato com a família." : "Os contatos registrados com as famílias aparecerão aqui."} />
        ) : (
          <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="alerts-list">
            {data.alerts.map((a) => <AlertCard key={a.id} a={a} onContact={setContactFor} />)}
          </div>
        )}
      <FamilyContactDialog studentId={contactFor?.student_id}
        description={contactFor ? `${contactFor.student_name} faltou 3 dias letivos seguidos.` : ""}
        open={!!contactFor} onClose={() => setContactFor(null)} onSaved={load} />
    </div>
  );
}
